import React from 'react';
import Data from 'data';
import Store from 'store';
import Locale from 'data/locale';
import { keyedList } from 'utils';
import { formatNumber, _R } from 'utils/format';

import TooltipBuilder, { colored } from 'tooltips/builder';
import { getStats } from './stats';
import { cubeGetStat, cubeSlotValid } from './cube';

const _L = Locale("ui-skills.js", "skilldata");

export function getSkillBonus(charClass, skill, stats, type) {
  if (!skill) return;
  type = (type || "buffs");
  const info = Data.skills[charClass][skill[0]];
  let cur = null;
  if (info && info[type]) {
    if (typeof info[type] == "function") {
      cur = info[type](skill[1], stats);
    } else {
      cur = info[type][skill[1]];
    }
  }
  return cur;
}

export function getPassiveBonus(charClass, passive, stats) {
  const info = Data.passives[charClass][passive];
  let cur = null;
  if (info && info.buffs) {
    if (typeof info.buffs == "function") {
      cur = info.buffs(stats);
    } else {
      cur = info.buffs;
    }
  }
  return cur;
}

export function getFollowerBonus(id, stats, type) {
  if (!id) return;
  type = (type || "buffs");
  const info = Data.followerSkills[id];
  let cur = null;
  if (info && info[type]) {
    if (typeof info[type] == "function") {
      cur = info[type](stats);
    } else {
      cur = info[type];
    }
  }
  return cur;
}

export function execString(expr, stats, affix, params) {
  if (!expr) return 0;
  if (typeof expr !== "string") return expr;
  if (affix) {
    expr = expr.replace(/\$([1-9])/g, (m, index) => {
      if (stats.affixes[affix]) {
        return stats.affixes[affix].value[parseInt(index) - 1];
      } else if (parseInt(index) === 1) {
        return stats[affix];
      }
    });
  } else if (params) {
    expr = expr.replace(/\$([1-9])/g, (m, index) => {
      return params[parseInt(index) - 1].val;
    });
  }
  return stats.execString(expr);
}

export function formatObject(fmt, options) {
  const { stats } = options;
  if (!stats) {
    throw Error("no stats provided for formatObject");
  }

  const exec = expr => execString(expr, stats, options.affix, options.params);

  let base, avg;
  if (fmt.thorns) {
    avg = (stats.thorns || 0);
    base = {min: avg, max: avg};
  } else if (fmt.weapon) {
    if (!stats.info[fmt.weapon]) return;
    base = Object.assign({}, stats.info[fmt.weapon].wpnbase);
    avg = (base.min + base.max) * 0.5;
  } else {
    base = Object.assign({}, stats.info.mainhand.wpnbase);
    avg = (base.min + base.max) * 0.5;
    if (stats.info.offhand) {
      base.min = Math.min(base.min, stats.info.offhand.wpnbase.min);
      base.max = Math.max(base.max, stats.info.offhand.wpnbase.max);
      avg = avg * 0.5 + (stats.info.offhand.wpnbase.min + stats.info.offhand.wpnbase.max) * 0.25;
    }
  }

  let total_coeff = 1;
  let extra_coeff = 0;

  const factors = [];
  // primary attribute
  factors.push({
    name: Data.stats[Data.classes[stats.charClass].primary].name,
    percent: (fmt.thorns === "bad" ? 0.25 : 1) * stats.info.primary,
  });
  // ethereal class bonus
  if (stats[`damage_${stats.charClass}`]) {
    factors.push({
      name: Data.stats[`damage_${stats.charClass}`].name,
      percent: stats[`damage_${stats.charClass}`],
    });
  }
  // attack speed scaling
  if (fmt.aps) {
    factors.push({
      name: "Attack speed",
      factor: (fmt.weapon ? stats.info[fmt.weapon].speed : stats.info.aps) * (fmt.aps === true ? 1 : exec(fmt.aps)),
    });
  }
  // skill multiplier
  if (fmt.coeff || fmt.addcoeff) {
    let coeff = (fmt.coeff ? exec(fmt.coeff) : 0);
    const basec = coeff;
    let addcoeff = undefined;
    total_coeff = coeff;
    if (fmt.addcoeff) {
      addcoeff = [];
      for (let i = 0; i < fmt.addcoeff.length; ++i) {
        let cur = fmt.addcoeff[i];
        let curval;
        if (typeof cur === "object") {
          cur = [exec(cur[0]), exec(cur[1])];
          if (cur[0] && cur[1]) addcoeff.push([100 * cur[0], cur[1]]);
          curval = cur[0] * cur[1];
        } else {
          cur = exec(cur);
          if (cur) addcoeff.push(100 * cur);
          curval = cur;
        }
        if (typeof fmt.total !== "number" || i < fmt.total) {
          total_coeff += curval;
        } else {
          extra_coeff += curval;
        }
        coeff += curval;
      }
    }
    factors.push({
      name: "Skill multiplier",
      factor: coeff,
      percent: 100 * basec,
      extra: addcoeff,
      coeff: true,
    });
  }
  // multiplicative factors = x<Value>
  if (fmt.factors) {
    for (const name in fmt.factors) {
      const factor = exec(fmt.factors[name]);
      if (factor !== 1) {
        factors.push({name, factor});
      }
    }
  }
  // multiplicative factor = /<Value>
  if (fmt.divide) {
    for (const name in fmt.divide) {
      const divide = exec(fmt.divide[name]);
      if (divide !== 1) {
        factors.push({name, factor: 1 / divide, divide});
      }
    }
  }
  // multiplicative factor = +<Value>%
  if (fmt.percent) {
    for (const name in fmt.percent) {
      const percent = exec(fmt.percent[name]);
      if (percent) {
        factors.push({name, percent});
      }
    }
  }
  // multiplicative passive = +<Value>%
  if (fmt.passives) {
    for (const p in fmt.passives) {
      if (stats.passives[p]) {
        const percent = exec(fmt.passives[p]);
        if (percent) {
          factors.push({name: Data.passives[stats.charClass][p].name, percent});
        }
      }
    }
  }

  // additive bonuses
  let bonuses = {};
  // skill damage
  const skillid = (fmt.skill || (options.skill && options.skill[0]));
  if (skillid && fmt.thorns !== "special" && !fmt.manald) {
    const bonus = stats[`skill_${stats.charClass}_${skillid}`];
    if (bonus) {
      bonuses["Skill %"] = bonus;
    }
  }
  // additive bonuses
  if (fmt.bonuses) {
    for (const b in fmt.bonuses) {
      const value = exec(fmt.bonuses[b]);
      if (value) {
        bonuses[b] = value;
      }
    }
  }
  // additive passives - those should apply directly to skill damage
  if (fmt.addpassives) {
    for (const p in fmt.addpassives) {
      if (stats.passives[p]) {
        const value = exec(fmt.addpassives[p]);
        if (value) {
          bonuses[Data.passives[stats.charClass][p].name] = value;
        }
      }
    }
  }
  // for pets, these bonuses apply as a separate factor from DIBS
  if ((fmt.pet || fmt.petspecial) && !fmt.sentry && Object.keys(bonuses).length) {
    factors.push({
      name: "Skill damage",
      percent: bonuses,
    });
    bonuses = {};
  }

  const elem = (fmt.elem === "max" ? stats.info.maxelem : fmt.elem);
  const srcelem = ((fmt.srcelem === "max" ? stats.info.maxelem : fmt.srcelem) || elem);
  //if (fmt.thorns === "normal") elem = "phy";
  // DIBS
  let tmp = stats.getTotalSpecial("damage", elem, fmt.pet, skillid, fmt.exclude);
  if (tmp) bonuses["Buffs"] = tmp;
  // debuffs
  tmp = stats.getTotalSpecial("dmgtaken", elem, fmt.pet, skillid, fmt.exclude);
  if (tmp && (fmt.thorns !== "normal" && fmt.thorns !== "bad")) {
    bonuses["Debuffs"] = tmp;
  }
  if (Object.keys(bonuses).length) {
    factors.push({
      name: (fmt.pet || fmt.petspecial ? "Buffs/debuffs" : "Damage increased by skills"),
      percent: bonuses,
    });
  }

  if (tmp && (fmt.thorns === "normal" || fmt.thorns === "bad")) {
    factors.push({
      name: Data.stats.dmgtaken.name,
      percent: tmp + (stats.thorns_taken || 0),
    });
  }

  // elemental/pet damage
  if (((srcelem && stats["dmg" + srcelem]) || (fmt.pet && stats.petdamage)) && !fmt.manald/*&& fmt.thorns !== "special"*/) {
    bonuses = {};
    if (srcelem && stats["dmg" + srcelem]) {
      bonuses[Data.elements[srcelem]] = stats["dmg" + srcelem];
    }
    if (srcelem === "col" && (fmt.pet || fmt.petspecial) && stats.leg_frostburn_p2) {
      bonuses[Data.itemById.P41_Unique_Gloves_002.name] = stats.leg_frostburn_p2;
    }
    if (fmt.pet && stats.petdamage) {
      bonuses["Pets"] = stats.petdamage;
    }
    factors.push({
      name: "Elemental damage",
      percent: bonuses,
    });
  }

  // elite damage
  if (Data.getOption("showElites", options.state) && stats.edmg) {
    let edmg = stats.edmg;
    if (fmt.manald) {
      edmg -= (stats.leg_thefurnace || 0);
      if (stats.gem_powerful_25) edmg -= 15;
    }
    if (edmg) factors.push({
      name: Data.stats.edmg.name,
      percent: edmg,
    });
  }

  // boss damage
  if (Data.getOption("targetBoss", options.state) && Data.getOption("showElites", options.state) && stats.bossdmg) {
    factors.push({
      name: Data.stats.bossdmg.name,
      percent: stats.bossdmg,
    });
  }

  // special damage
  const targetType = Data.getOption("targetType", options.state);
  if (targetType && stats["damage_" + targetType]) {
    factors.push({
      name: Data.stats["damage_" + targetType].name,
      percent: stats["damage_" + targetType],
    });
  }

  // global multipliers
  for (const [name, percent] of stats.getSpecial("dmgmul", elem, fmt.pet, skillid, fmt.exclude)) {
    factors.push({name, percent});
  }

  let total_factor = 1;
  for (const factor of factors) {
    if (!factor.factor) {
      let percent = 0;
      if (typeof factor.percent === "number") {
        percent = factor.percent;
      } else {
        for (const id in factor.percent) {
          percent += factor.percent[id];
        }
      }
      factor.factor = 1 + 0.01 * percent;
    }
    total_factor *= factor.factor;
  }

  let bonus_chc = exec(fmt.chc);
  let bonus_chd = exec(fmt.chd);
  let chc;
  if (fmt.total) {
    chc = Math.min(1, 0.01 * (stats.chc + bonus_chc));
  } else {
    chc = Math.min(1, 0.01 * (stats.final.chc + bonus_chc * (1 + 0.01 * (stats.chctaken_percent || 0))));
  }
  let chd = 0.01 * (stats.chd + bonus_chd);
  if (fmt.thorns/* === "normal"*/) {
    if (stats.thorns_chc) {
      chc = 1;
      bonus_chc = 1;
    } else {
      chc = chd = bonus_chc = 0;
    }
  }

  var value = avg * total_factor * (1 + chc * chd);

  return {
    factors,
    total_factor: total_factor * total_coeff / (total_coeff + extra_coeff),
    extra_factor: total_factor * extra_coeff / (total_coeff + extra_coeff),
    value,
    base,
    chc,
    chd,
    bonus_chc,
  };
}

export function calcFramesFull(stats, fpa, slowest, weapon, dspeed, method) {
  const delta = (method === "up" ? 1 : 0);
  const options = {fpa: fpa, dspeed: (dspeed || 1), fpadelta: delta};
  if (!weapon && stats.info.offhand) {
    if (slowest) {
      options.basespeed = Math.min(stats.info.mainhand.speed, stats.info.offhand.speed);
      options.frames = Math.floor(fpa / (options.dspeed * options.basespeed)) + delta;
    } else {
      options.framesmh = Math.floor(fpa / (options.dspeed * stats.info.mainhand.speed)) + delta;
      options.framesoh = Math.floor(fpa / (options.dspeed * stats.info.offhand.speed)) + delta;
      options.frames = 0.5 * (options.framesmh + options.framesoh);
    }
  } else {
    options.basespeed = stats.info[weapon || "mainhand"].speed;
    options.frames = Math.floor(fpa / (options.dspeed * options.basespeed)) + delta;
  }
  return options;
}

export function calcFrames(stats, fpa, slowest, weapon, dspeed, method) {
  return calcFramesFull(stats, fpa, slowest, weapon, dspeed, method).frames;
}

class BreakpointsTooltipBuilder extends TooltipBuilder {
  breakpoints(stats, options) {
    if (options.pet) {
      if (options.frames > 6) {
        const basemh = stats.info.mainhand.speed / (1 + 0.01 * (stats.ias || 0));
        const apsneed = options.fpa / (options.frames - 5) / options.speed;
        const iasneed = Math.ceil((100 * apsneed / basemh - 100 - (stats.ias || 0)) * 10) / 10;
        this.line(_L`IAS to next breakpoint: ${colored(iasneed + "%", "white")}`, false);
      }

      const rows = [];
      for (let curfpa = Math.ceil(Math.floor(options.fpa) / 6) * 6; curfpa >= 6; curfpa -= 6) {
        const curaps = Math.max(1, options.fpa / (curfpa + 1) / options.speed + 0.000051);
        if (options.speed / (curfpa + 1) > 5) break;
        const delta = 100 * (options.frames / curfpa - 1);
        const color = (delta < 0 ? "red" : (delta > 0 ? "green" : "gray"));
        rows.push(
          <tr key={curfpa} className={curfpa === options.frames ? "current" : null}>
            <td>{curfpa}</td>
            <td>{curaps.toFixed(4)}</td>
            <td>{Math.max(1, options.fpa / (curfpa + 1) + 0.000051).toFixed(4)}</td>
            <td className={`d3-color-${color}`}>{delta < 0 ? delta.toFixed(1) : "+" + delta.toFixed(1)}%</td>
          </tr>
        );
      }
      this.body.push(
        <table>
          <thead>
            <tr><th>{_L`FPA`}</th><th>{_L`APS`}</th><th>{_L`Pet APS`}</th><th>{_L`Delta`}</th></tr>
          </thead>
          <tbody>
            {rows}
          </tbody>
        </table>
      );
    } else {
      function fpaDelta(nextmh, nextoh) {
        let fpa;
        if (options.framesmh && options.framesoh) {
          fpa = 0.5 * (Math.floor(options.fpa / (options.dspeed * nextmh)) + Math.floor(options.fpa / (options.dspeed * nextoh))) + options.fpadelta;
        } else {
          fpa = Math.floor(options.fpa / (options.dspeed * nextmh)) + options.fpadelta;
        }
        if (fpa < options.frames) {
          return colored(`+${formatNumber(100 * (options.frames / fpa - 1), 1)}%`, "green");
        } else if (fpa > options.frames) {
          return colored(`-${formatNumber(100 * (1 - options.frames / fpa), 1)}%`, "red");
        } else {
          return colored("+0%", "gray");
        }
      }

      if (options.framesmh && options.framesoh) {
        if (options.framesmh > 1 && options.framesoh > 1) {
          let nextmh = options.fpa / (options.framesmh - options.fpadelta) / options.dspeed + 0.000051;
          let nextoh = options.fpa / (options.framesoh - options.fpadelta) / options.dspeed + 0.000051;
          const next = Math.min(nextmh / stats.info.mainhand.speed, nextoh / stats.info.offhand.speed);
          nextmh = next * stats.info.mainhand.speed;
          nextoh = next * stats.info.offhand.speed;
          const iasneed = Math.ceil(10 * (next * (100 + (stats.ias || 0)) - 100 - (stats.ias || 0))) / 10;
          const APS = <React.Fragment>{colored(formatNumber(nextmh, 4, 10000), "white")}/{colored(formatNumber(nextoh, 4, 10000), "white")}</React.Fragment>;
          const IAS = colored(`+${iasneed}%`, "white");
          if (options.dps !== false) {
            this.line(_L`Next breakpoint: ${APS} APS (${IAS} IAS), ${fpaDelta(nextmh, nextoh)} DPS`, false);
          } else {
            this.line(_L`Next breakpoint: ${APS} APS (${IAS} IAS)`, false);
          }
        }

        let prevmh = options.fpa / (options.framesmh + 1 - options.fpadelta) / options.dspeed - 0.000051;
        let prevoh = options.fpa / (options.framesoh + 1 - options.fpadelta) / options.dspeed - 0.000051;
        const prev = Math.max(prevmh / stats.info.mainhand.speed, prevoh / stats.info.offhand.speed);
        prevmh = prev * stats.info.mainhand.speed;
        prevoh = prev * stats.info.offhand.speed;
        const iasneed = Math.ceil(10 * (prev * (100 + (stats.ias || 0)) - 100 - (stats.ias || 0))) / 10;
        const APS = <React.Fragment>{colored(formatNumber(prevmh, 4, 10000), "white")}/{colored(formatNumber(prevoh, 4, 10000), "white")}</React.Fragment>;
        const IAS = colored(`${iasneed}%`, "white");
        if (options.dps !== false) {
          this.line(_L`Previous breakpoint: ${APS} APS (${IAS} IAS), ${fpaDelta(prevmh, prevoh)} DPS`, false);
        } else {
          this.line(_L`Previous breakpoint: ${APS} APS (${IAS} IAS)`, false);
        }
      } else {
        if (options.frames > 1) {
          const next = options.fpa / (options.frames - options.fpadelta) / options.dspeed + 0.000051;
          const basemh = options.basespeed / (1 + 0.01 * (stats.ias || 0));
          const iasneed = Math.ceil(10 * (100 * next / basemh - 100 - (stats.ias || 0))) / 10;
          const APS = colored(formatNumber(next, 4, 10000), "white");
          const IAS = colored(`+${iasneed}%`, "white");
          if (options.dps !== false) {
            this.line(_L`Next breakpoint: ${APS} APS (${IAS} IAS), ${fpaDelta(next)} DPS`, false);
          } else {
            this.line(_L`Next breakpoint: ${APS} APS (${IAS} IAS)`, false);
          }
        }

        const prev = options.fpa / (options.frames + 1 - options.fpadelta) / options.dspeed - 0.000051;
        const basemh = options.basespeed / (1 + 0.01 * (stats.ias || 0));
        const iasneed = Math.ceil(10 * (100 * prev / basemh - 100 - (stats.ias || 0))) / 10;
        const APS = colored(formatNumber(prev, 4, 10000), "white");
        const IAS = colored(`${iasneed}%`, "white");
        if (options.dps !== false) {
          this.line(_L`Previous breakpoint: ${APS} APS (${IAS} IAS), ${fpaDelta(prev)} DPS`, false);
        } else {
          this.line(_L`Previous breakpoint: ${APS} APS (${IAS} IAS)`, false);
        }
      }

      if (!options.sequence) {
        let fpa0 = options.frames, fpa1 = options.frames;
        if (options.framesmh && options.framesoh) {
          fpa0 = options.framesmh;
          fpa1 = options.framesoh;
        }
        const list = [];
        let maxaps = Math.max(3, stats.info.mainhand.speed);
        if (options.framesoh) {
          maxaps = Math.max(maxaps, stats.info.offhand.speed);
        }
        maxaps = Math.max(maxaps * 1.2, maxaps + 0.5);
        for (let curfpa = Math.floor(options.fpa) + options.fpadelta; curfpa >= 1; curfpa -= 1) {
          const curaps = Math.max(1, options.fpa / (curfpa + 1 - options.fpadelta) / options.dspeed + 0.000051);
          if (curaps > maxaps && (list.length % 3) === 0) break;
          /*if (fpa != Math.floor(data.fpa) + fpadelta && fpa != 1 && fpa != fpa0 && fpa != fpa1 &&
              fpa % 5 != 0 && Math.abs(fpa - fpa0) > 2 && Math.abs(fpa - fpa1) > 2) {
            continue;
          }*/
          list.push([curfpa, curaps.toFixed(4)]);
        }
        const rows = [];
        const fcol = Math.ceil(list.length / 3);
        for (let row = 0; row < fcol; ++row) {
          const cell = (i, [fpa, aps]) => {
            const cls = (fpa === fpa0 || fpa === fpa1 ? "current" : null);
            return [<td key={i} className={cls}>{fpa}</td>, <td key={i + 1} className={cls}>{aps}</td>];
          };

          const cols = cell(0, list[row]);
          if (row + fcol < list.length) {
            cols.push(<td key={2}/>, ...cell(3, list[row + fcol]));
          }
          if (row + fcol + fcol < list.length) {
            cols.push(<td key={5}/>, ...cell(6, list[row + fcol + fcol]));
          }

          rows.push(<tr key={row}>{cols}</tr>);
        }
        const hFPA = <th>{_L`FPA`}</th>;
        const hAPS = <th>{_L`APS`}</th>;
        this.body.push(
          <table className="d3p-col2">
            <thead>
              <tr>{hFPA}{hAPS}<th/>{hFPA}{hAPS}<th/>{hFPA}{hAPS}</tr>
            </thead>
            <tbody>
              {rows}
            </tbody>
          </table>
        );
      }
    }
  }
}

function Val(val, decimal, clr, per) {
  const parts = ("" + parseFloat(val.toFixed(decimal || 0))).split(".");
  if (Math.abs(parseFloat(val)) >= 10000) {
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  val = parts.join(".");
  if (per && per.indexOf("+") >= 0) {
    if (val > 0) val = "+" + val;
    per = per.replace("+", "");
  }
  return colored(val + (per || ""), clr || "green");
}

function Rng(a, b, clr) {
  a = Math.floor(a);
  b = Math.floor(b);
  if (a === b) {
    return Val(a, 0, clr);
  } else {
    return <React.Fragment>{Val(a, 0, clr)}-{Val(b, 0, clr)}</React.Fragment>;
  }
}

export function processInfo(lines, options) {
  let { notext, notips, stats } = options;
  if (!stats) {
    throw Error("no stats provided for formatObject");
  }
  if (notext) {
    notips = true;
  }

  const exec = expr => execString(expr, stats, options.affix, options.params);

  const profile = options.state ? options.state.profiles[options.state.activeProfile] : null;

  const result = {};
  let sumlines = [];

  const breakpointTip = new BreakpointsTooltipBuilder();
  const breakpointValues = [];

  for (const [stat, fmt] of Object.entries(lines)) {
    if (!fmt) continue;
    result[stat] = {};
    if (typeof fmt !== "object") {
      // OPTION 1
      // plain text string
      if (typeof fmt === "number") {
        result[stat].value = fmt;
      } else {
        if (!notext) result[stat].text = fmt;
      }
    } else if (fmt.sum) {
      // OPTION 2
      // sum = true, queue for processing later
      sumlines.push(stat);
    } else if (fmt.cooldown != null && fmt.duration != null) {
      // OPTION 3
      // cooldown AND duration, skill, cdr, after, tip 
      const skill = fmt.skill || (options.skill && options.skill[0]);
      const duration = exec(fmt.duration);
      let cooldown = exec(fmt.cooldown);
      if (skill) {
        cooldown -= (stats[`skill_${stats.charClass}_${skill}_cooldown`] || 0);
      }
      if (fmt.cdr) {
        cooldown *= 1 - 0.01 * exec(fmt.cdr);
      }
      cooldown = Math.max(0.5, cooldown * (1 - 0.01 * (stats.cdr || 0)) - (stats.cdrint || 0));

      let value;
      if (cooldown <= 0.01) {
        value = 100;
      } else {
        value = Math.min(100, 100 * duration / (cooldown + (fmt.after ? duration : 0)));
      }
      result[stat].value = value;

      if (!notext) {
        result[stat].text = formatNumber(value, 0, 10000) + "%";
      }
      if (!notips) {
        const tip = new TooltipBuilder(_L(stat), result[stat].text);
        tip.add(fmt.tip);
        tip.line(_L`Duration: ${Val(duration, 0, "white")} seconds`);
        tip.line(_L`Cooldown: ${Val(cooldown, 2, "white")} seconds`);
        if (fmt.after) {
          tip.line(_L`Downtime: ${Val(100 - value, 2, "white", "%")}`);
          tip.line(_L`Cooldown does not start until the effect expires.`);
        } else {
          tip.line(_L`Downtime: ${Val(100 - value, 2, "white", "%")} (${Val(Math.max(0, cooldown - duration), 2, "white")} seconds)`);
        }
        result[stat].tip = tip.build();
      } else if (!notext) {
        result[stat].tip = "";
      }
    } else if (fmt.cooldown != null) {
      // OPTION 4
      // cooldown, skill, cdr (no tip)
      let cooldown = exec(fmt.cooldown);
      if (!cooldown) {
        delete result[stat];
        continue;
      }
      const skill = fmt.skill || (options.skill && options.skill[0]);
      if (skill) {
        cooldown -= (stats[`skill_${stats.charClass}_${skill}_cooldown`] || 0);
      }
      if (fmt.cdr) {
        cooldown *= 1 - 0.01 * exec(fmt.cdr);
      }
      cooldown = Math.max(0.5, cooldown * (1 - 0.01 * (stats.cdr || 0) - (stats.cdrint || 0)));
      result[stat].value = cooldown;
      if (!notext) result[stat].text = _L`${parseFloat(cooldown.toFixed(2))} seconds`;
    } else if (fmt.duration != null) {
      // OPTION 4a
      // just duration
      const duration = exec(fmt.duration);
      result[stat].value = duration;
      if (!notext) result[stat].text = _L`${parseFloat(duration.toFixed(2))} seconds`;
    } else if (fmt.cost != null) {
      // OPTION 5
      // cost, rcr, skill, speed/fpa, weapon, round
      const resource = (fmt.resource || Data.classes[stats.charClass].resources[0]);
      let cost = exec(fmt.cost);
      const cost_initial = cost;
      const rcr = {}, rcrint = {};
      cost *= 1 - 0.01 * (stats[`rcr_${resource}`] || 0);
      if (fmt.rcr) {
        if (typeof fmt.rcr === "object") {
          for (let id in fmt.rcr) {
            const value = exec(fmt.rcr[id]);
            if (value) {
              cost *= 1 - 0.01 * value;
              if (!notips) {
                if (stats.affixes[id]) id = stats.getAffixSource(id, profile);
                rcr[Data.sourceNames[id] || id] = value;
              }
            }
          }
        } else {
          const value = exec(fmt.rcr);
          if (value) {
            cost *= 1 - 0.01 * value;
            if (!notips) rcr[_L`Unknown`] = value;
          }
        }
      }
      if (options.skill && options.skill[0]) {
        const skill = Data.skilltips[stats.charClass][options.skill[0]];
        if (skill && skill.elements[options.skill[1]] === "fir") {
          cost *= 1 - 0.01 * (stats.leg_cindercoat || 0);
          if (!notips && stats.leg_cindercoat && stats.affixes.leg_cindercoat) {
            rcr[Data.sourceNames[stats.getAffixSource("leg_cindercoat", profile)]] = stats.leg_cindercoat;
          }
        }
      }
      const skillid = fmt.skill || (options.skill && options.skill[0]);
      if (skillid) {
        const id = `skill_${stats.charClass}_${skillid}_cost`;
        if (stats[id]) {
          cost = Math.max(0, cost - (stats[id] || 0));
          if (!notips && stats[id]) rcrint[Data.sourceNames[id] || id] = stats[id];
        }
      }
      cost = Math.max(0, cost - (stats.rcrint || 0));
      if (!notips && stats.sources.rcrint) {
        for (const id in stats.sources.rcrint) {
          if (Data.sourceNames[id]) rcrint[Data.sourceNames[id]] = stats.sources.rcrint[id];
        }
      }
      if (cost === 0) {
        delete result[stat];
        continue;
      }

      let formula, tip_lines;
      if (!notips) {
        formula = [Val(cost_initial, 2, "white")];
        tip_lines = [_L`Base cost: ${Val(cost_initial, 2, "white")}`];
        const rcr_r = `rcr_${resource}`;
        if (stats[rcr_r]) {
          const color = (stats[rcr_r] > 0 ? "green" : "red");
          formula.push(" \u00d7 ", Val(1 - 0.01 * stats[rcr_r], 4, color));
          const statName = (stats[rcr_r] === stats.rcr ? "rcr" : rcr_r);
          tip_lines.push(_R`${Data.stats[statName].name}: ${Val(-stats["rcr_" + resource], 2, color, "+%")}`);
        }
        for (const id in rcr) {
          const color = (rcr[id] > 0 ? "green" : "red");
          formula.push(" \u00d7 ", Val(1 - 0.01 * rcr[id], 4, color));
          tip_lines.push(_R`${id}: ${Val(-rcr[id], 2, color, "+%")}`);
        }
        for (const id in rcrint) {
          const color = (rcrint[id] > 0 ? "green" : "red");
          formula.push(rcrint[id] > 0 ? " - " : " + ", Val(Math.abs(rcrint[id]), 2, color));
          tip_lines.push(_R`${id}: ${Val(-rcrint[id], 2, color, "+")}`);
        }
      }

      if (fmt.speed || fmt.fpa) {
        const dspeed = (exec(fmt.speed) || 1) * (1 + 0.01 * exec(fmt.ias));
        let aps, fpadata;
        if (fmt.fpa) {
          fpadata = calcFramesFull(stats, fmt.fpa, !!fmt.slowest, fmt.weapon, dspeed, fmt.round);
          aps = 60 / fpadata.frames;
          cost *= fmt.fpa / 60;
        } else {
          aps = dspeed * (fmt.weapon ? stats.info[fmt.weapon].speed : stats.info.aps);
        }
        result[stat].value = cost * aps;

        if (!notext) {
          result[stat].text = _L`${parseFloat((cost * aps).toFixed(2))} ${Data.resources[resource]} per second`;
          if (fmt.fpa && fmt.bp) {
            breakpointValues.push(fpadata.frames);
          }
        }
        if (!notips) {
          if (fmt.fpa) {
            const tip = new TooltipBuilder(_L(stat), result[stat].text);
            tip.add(fmt.tip);
            tip.line(_L`Cost per tick: ${Val(cost, 2, "white")} ${Data.resources[resource]}`);

            function _gcd(a, b) { return (b ? _gcd(b, a % b) : a); }
            const gcd = _gcd(60, fmt.fpa);
            if (Object.keys(rcrint).length) {
              formula.unshift("(");
              formula.push(")");
            }
            if (fmt.fpa > gcd) {
              formula.push(" \u00d7 ", Val(fmt.fpa / gcd, 0, "white"));
            }
            formula.push(" / ", Val(60 / gcd, 0, "white"));

            tip.line(_L`Formula: ${keyedList(formula)}`);
            for (const line of tip_lines) {
              tip.line(line, false);
            }
            const factor = <React.Fragment>{Val(fmt.fpa / gcd, 0, "white")} / {Val(60 / gcd, 0, "white")}</React.Fragment>;
            tip.line(_L`Base tick rate factor: ${factor}`, false);

            tip.line(_L`Breakpoint: ${fpadata.frames} frames (${Val(aps, 2, "white")} APS)`);
            result[stat].tip = tip.build();

            if (fmt.bp) {
              breakpointTip.line(_L`${_L`Tick rate`}: ${Val(fpadata.frames, 0, "white")} frames (${Val(aps, 2, "white")} APS)`);
              fpadata.dps = false;
              breakpointTip.breakpoints(stats, fpadata);
            }
          }
        } else if (!notext && fmt.fpa) {
          result[stat].tip = "";
        }
      } else {
        result[stat].value = cost;
        if (!notext) {
          if (fmt.persecond) {
            result[stat].text = _L`${parseFloat(cost.toFixed(2))} ${Data.resources[resource]} per second`;
          } else {
            result[stat].text = _L`${parseFloat(cost.toFixed(2))} ${Data.resources[resource]}`;
          }
        }
        if (!notips) {
          const tip = new TooltipBuilder(_L(stat), result[stat].text);
          tip.add(fmt.tip);
          tip.line(_L`Formula: ${keyedList(formula)}`);
          for (const line of tip_lines) {
            tip.line(line, false);
          }
          result[stat].tip = tip.build();
        } else if (!notext) {
          result[stat].tip = "";
        }
      }
    } else if (fmt.value != null) {
      // OPTION 6
      // value (just value), tip
      if (typeof fmt.value === "number") {
        result[stat].value = fmt.value;
        if (!notext) result[stat].text = formatNumber(fmt.value, 0, 10000);
      } else if (!notext) {
        result[stat].text = _L`fmt.value`;
      }
      if (fmt.tip && !notips) {
        result[stat].tip = (new TooltipBuilder(_L(stat), result[stat].text)).add(fmt.tip).build();
      } else if (fmt.tip && !notext) {
        result[stat].tip = "";
      }
    } else {
      // OPTION 7
      // damage!

      const data = formatObject(fmt, options);
      if (!data) {
        delete result[stat];
        continue;
      }
      result[stat].value = data.value;

      if (!notext) {
        result[stat].text = formatNumber(data.value, 0, 10000);
      }
      if (!notips) {
        const tip = new TooltipBuilder(fmt.total === true ? _L(stat) : _L`Average ${_L(stat)}`, result[stat].text);
        tip.add(fmt.tip);
        if (!fmt.weapon && stats.info.mainhand && stats.info.offhand) {
          tip.line(_L`Alternates weapons`);
        }
        if (!fmt.thorns && fmt.total !== true) {
          if (fmt.nocrit) {
            tip.line(_L`Damage range: ${Rng(data.base.min * data.total_factor * (1 + data.chc * data.chd),
                                            data.base.max * data.total_factor * (1 + data.chc * data.chd), "white")}`);
          } else {
            if (!fmt.weapon && stats.info.mainhand && stats.info.offhand) {
              tip.line(_L`Mainhand white damage: ${Rng(stats.info.mainhand.wpnbase.min * data.total_factor,
                                                       stats.info.mainhand.wpnbase.max * data.total_factor, "white")}`);
              tip.line(_L`Offhand white damage: ${Rng(stats.info.offhand.wpnbase.min * data.total_factor,
                                                      stats.info.offhand.wpnbase.max * data.total_factor, "white")}`);
              if (data.bonus_chc) {
                tip.line(_L`Critical chance: ${Val(data.chc * 100, 2, "green", "%")}`);
              }
              tip.line(_L`Mainhand critical damage: ${Rng(stats.info.mainhand.wpnbase.min * data.total_factor * (1 + data.chd),
                                                          stats.info.mainhand.wpnbase.max * data.total_factor * (1 + data.chd), "white")}`);
              tip.line(_L`Offhand critical damage: ${Rng(stats.info.offhand.wpnbase.min * data.total_factor * (1 + data.chd),
                                                         stats.info.offhand.wpnbase.max * data.total_factor * (1 + data.chd), "white")}`);
            } else {
              tip.line(_L`White damage: ${Rng(data.base.min * data.total_factor,
                                              data.base.max * data.total_factor, "white")}`);
              if (data.bonus_chc) {
                tip.line(_L`Critical chance: ${Val(data.chc * 100, 2, "green", "%")}`);
              }
              tip.line(_L`Critical damage: ${Rng(data.base.min * data.total_factor * (1 + data.chd),
                                                 data.base.max * data.total_factor * (1 + data.chd), "white")}`);
            }
          }
        }
        if (typeof fmt.total === "number") {
          tip.line(_L`Extra damage: ${Val(0.5 * (data.base.min + data.base.max) * data.extra_factor * (1 + data.chc * data.chd), 0, "white")}`);
        }
        const formula = [colored(fmt.thorns ? _L`[Thorns]` : _L`[Weapon]`, "gray")];
        if (fmt.nocrit || fmt.total) {
          formula.push(" \u00d7 ", Val(1 + data.chc * data.chd, 2));
        }
        for (const factor of data.factors) {
          if (factor.divide) {
            formula.push(" / ", Val(factor.divide, 2));
          } else {
            formula.push(" \u00d7 ", Val(factor.factor, 2));
          }
        }
        tip.line(_L`Formula: ${keyedList(formula)}`);
        if (fmt.thorns) {
          tip.line(_L`Thorns damage: ${Val(stats.thorns || 0, 0, "white")}`, false);
        } else {
          if (fmt.weapon !== "offhand") {
            if (!fmt.avg) {
              tip.line(_L`Main hand damage: ${Rng(stats.info.mainhand.wpnbase.min, stats.info.mainhand.wpnbase.max, "white")}`, false);
            } else {
              tip.line(_L`Average main hand damage: ${Val(0.5 * (stats.info.mainhand.wpnbase.min + stats.info.mainhand.wpnbase.max), 0, "white")}`, false);
            }
          }
          if (fmt.weapon !== "mainhand" && stats.info.offhand) {
            tip.line(_L`Off hand damage: ${Rng(stats.info.offhand.wpnbase.min, stats.info.offhand.wpnbase.max, "white")}`, false);
          }
        }
        if (data.chd) {
          if (fmt.nocrit || fmt.total) {
            tip.line(_L`Critical multiplier: ${<React.Fragment>{Val(1, 0, "white")} + {Val(data.chc, 2, "green")} &#215; {Val(data.chd, 2, "green")}</React.Fragment>}`, false);
          } else {
            tip.line(_L`Critical damage: ${Val(100 * data.chd, 1, "white", "%")}`, false);
          }
        }
        for (const factor of data.factors) {
          const entries = [];
          if (factor.divide) {
            entries.push(Val(1, 0, "white"), " / ", Val(factor.divide, 2));
          } else if (factor.percent != null) {
            if (typeof factor.percent === "number") {
              if (factor.percent) {
                entries.push(Val(factor.percent, 2, "green", "%"));
              }
            } else {
              for (const f in factor.percent) {
                if (entries.length) entries.push(" + ");
                entries.push(Val(factor.percent[f], 2, "green", "%"), " ", colored(`(${_L(f)})`, "gray"));
              }
            }
          } else {
            entries.push(Val(factor.factor, 2));
          }
          if (factor.extra) {
            for (const extra of factor.extra) {
              if (typeof extra === "object") {
                entries.push(" + ", Val(extra[0], 2, "green", "%"), " \u00d7 ", Val(extra[1], 2, "green"));
              } else {
                entries.push(" + ", Val(extra, 2, "green", "%"));
              }
            }
          }
          tip.line(_R`${_L(factor.name)}: ${keyedList(entries)}`);
        }

        result[stat].tip = tip.build();
      } else if (!notext) {
        result[stat].tip = "";
      }
    }
  }

  const speedCache = {};
  while (sumlines.length) {
    const next = [];
    for (const stat of sumlines) {
      let okay = true;
      let sum = 0;
      const tip = new TooltipBuilder();
      const bpTip = new BreakpointsTooltipBuilder();
      const bpValues = [];
      const sequence = (lines[stat].sum === "sequence");
      let totalTime = 0;
      for (let [src, data] of Object.entries(lines[stat])) {
        if (!result[src] && (typeof data !== "object" || src === "tip")) {
          continue;
        }
        let key = src;
        if (typeof data === "object" && src !== "tip") {
          key = (data.src || src);
        }
        if ((!result[key] || result[key].value === undefined) && (typeof data !== "object" || !data.value)) {
          okay = false;
          break;
        }
        if (typeof data === "object") {
          data = Object.assign({}, data);
        } else {
          data = {speed: data};
        }
        const line = [_L(data.name || src) + ": "];
        let mul = 1;
        if (data.count) {
          data.count = exec(data.count);
          if (data.count && data.count !== 1) {
            mul *= data.count;
            if (!notips) line.push(parseFloat(data.count.toFixed(2)) + "x ");
          }
        }
        const theValue = (data.value ? exec(data.value) : result[key].value);
        if (!notips) line.push(Val(theValue, 0, "white"));
        if (data.factor) {
          data.factor = exec(data.factor);
          if (data.factor !== 1) {
            mul *= data.factor;
            if (!notips) line.push(" \u00d7 ", Val(data.factor, 2, "white"));
          }
        }
        if (data.divide) {
          data.divide = exec(data.divide);
          if (data.divide !== 1) {
            mul /= data.divide;
            if (!notips) line.push(" / ", Val(data.divide, 0, "white"));
          }
        }
        if (!notips) tip.line(keyedList(line));
        if (data.cd) {
          data.cd = exec(data.cd);
          if (data.cdr !== false) {
            data.cd -= (stats.cdrint || 0);
            data.cd *= (1 - 0.01 * (stats.cdr || 0));
          }
          data.cd = Math.max(0.5, data.cd);
          if (!notips) tip.line(_L`Cooldown: ${Val(data.cd, 2, "white")} seconds`, false);
          mul /= data.cd;
        }
        if (data.pet) {
          let frames = null;
          let aps = (data.speed ? exec(data.speed) * stats.info.mainhand.speed : (data.aps ? exec(data.aps) : 1));
          let petias = (stats.petias || 0);
          if (data.area === false) {
            petias = (stats.leg_taskerandtheo || 0);
          }
          const tnt = 1 + 0.01 * exec(data.ias) + 0.01 * petias;
          aps *= tnt;
          if (typeof data.pet === "number") {
            frames = Math.ceil(Math.floor(data.pet / aps) / 6) * 6;
            aps = 60 / frames;
          }
          if (!notext && frames && !data.nobp) {
            bpValues.push(frames);
          }
          if (!notips) {
            const c_aps = colored(formatNumber(aps, 2, 10000), "white");
            if (!frames) {
              tip.line(_L`Speed: ${c_aps}`, false);
            } else {
              tip.line(_L`Speed: ${c_aps} (${Val(frames, 0, "white")} frames)`, false);
              if (!data.nobp) {
                bpTip.line(_L`${_L(data.name || src)}: ${Val(frames, 0, "white")} frames (${c_aps} APS)`);
                if (data.speed) {
                  bpTip.breakpoints(stats, {
                    pet: true,
                    fpa: data.pet,
                    frames: frames,
                    speed: tnt * exec(data.speed),
                  });
                }
              }
            }
          }
          if (sequence) {
            totalTime += 1 / aps;
          } else {
            if (data.cd) {
              mul *= Math.min(1, data.cd * aps);
            } else {
              mul *= aps;
            }
          }
        } else if (data.speed || data.aps || data.fpa) {
          let aps = 1;
          let weapon = null;
          let dspeed = null;
          if (data.speed) {
            weapon = (typeof lines[key] === "object" && lines[key].weapon);
            dspeed = exec(data.speed);
            aps = dspeed * (weapon ? stats.info[weapon].speed : stats.info.aps);
          } else if (data.aps) {
            aps = exec(data.aps);
          }
          if (data.ias) {
            const ias = 1 + 0.01 * exec(data.ias);
            aps *= ias;
            if (dspeed) dspeed *= ias;
          }
          let fpadata = null;
          if (data.fpa) {
            if (data.speed) {
              fpadata = calcFramesFull(stats, data.fpa, !!data.slowest, weapon, dspeed, data.round);
            } else {
              fpadata = {frames: Math.floor(data.fpa / aps) + (data.round === "up" ? 1 : 0)};
            }
            aps = 60 / fpadata.frames;
          }
          if (!notext && fpadata && !data.nobp) {
            bpValues.push(fpadata.frames);
          }
          if (!notips) {
            const c_aps = colored(formatNumber(aps, 2, 10000), "white");
            if (!fpadata) {
              tip.line(_L`Speed: ${c_aps}`, false);
            } else {
              tip.line(_L`Speed: ${c_aps} (${Val(fpadata.frames, 1, "white")} frames)`, false);
              if (!data.nobp) {
                bpTip.line(_L`${_L(data.name || src)}: ${Val(fpadata.frames, 0, "white")} frames (${c_aps} APS)`);
                if (data.speed) {
                  fpadata.sequence = sequence;
                  bpTip.breakpoints(stats, fpadata);
                }
                if (data.total) {
                  let ticks;
                  if (fpadata.framesmh && fpadata.framesoh) {
                    const whole = Math.floor(data.total / (fpadata.framesmh + fpadata.framesoh));
                    const remain = data.total - whole * (fpadata.framesmh + fpadata.framesoh);
                    ticks = whole * 2;
                    if (remain) {
                      ticks += (remain > fpadata.framesmh ? 1 : 0.5) + (remain > fpadata.framesoh ? 1 : 0.5);
                    }
                  } else {
                    ticks = Math.ceil(data.total / fpadata.frames);
                  }
                  const damage = colored(formatNumber(theValue * (mul * (data.cd || 1)) * ticks, 0, 10000), "green");
                  tip.line(_L`Total ticks: ${Val(ticks, 1, "white")} (${damage} damage)`, false);
                }
              }
            }
          }
          if (sequence) {
            totalTime += 1 / aps;
          } else {
            if (data.cd) {
              mul *= Math.min(1, data.cd * aps);
            } else {
              mul *= aps;
            }
          }
        } else if (data.refspeed) {
          if (speedCache[data.refspeed] === undefined) {
            okay = false;
            break;
          } else {
            if (data.cd) {
              mul *= Math.min(1, data.cd / speedCache[data.refspeed]);
            } else {
              mul /= speedCache[data.refspeed];
            }
            tip.line(_L`Speed: ${Val(1 / speedCache[data.refspeed], 2, "white")}`, false);
          }
        }
        sum += theValue * mul;
      }
      if (sequence && okay) {
        if (totalTime > 0.1) {
          sum /= totalTime;
          speedCache[stat] = totalTime;
          tip.prepend(_L`Sequence length: ${Val(totalTime, 2, "white")} seconds`);
        } else {
          okay = false;
        }
      }
      if (okay) {
        result[stat].value = sum;
        if (!notext) {
          result[stat].text = formatNumber(sum, 0, 10000);
        }
        if (!notext && !lines[stat].nobp) {
          breakpointValues.push(...bpValues);
        }
        if (!notips) {
          const tmptip = new TooltipBuilder(_L(stat).replace("DPS", _L`Damage Per Second`), result[stat].text);
          tmptip.add(lines[stat].tip);
          tmptip.add(tip);
          result[stat].tip = tmptip.build();
          if (!lines[stat].nobp) {
            breakpointTip.add(bpTip);
          }
        } else if (!notext) {
          result[stat].tip = "";
        }
      } else {
        next.push(stat);
      }
    }
    if (next.length >= sumlines.length) {
      break;
    }
    sumlines = next;
  }

  for (const stat of sumlines) {
    delete result[stat];
  }

  if (breakpointValues.length && !notext) {
    const title = (breakpointValues.length > 1 ? "Breakpoints" : "Breakpoint");
    const text = breakpointValues.map(value => _L`${value} frames`).join(" / ");
    result[title] = {
      value: breakpointValues,
      text: text,
      tip: "",
    };
    if (!notips) {
      breakpointTip.header(_L(title), text);
      result[title].tip = breakpointTip.build();
    }
  }

  return result;
}

class InfoCache {
  constructor(state, stats, info, values, affix, skill) {
    this.state = state;
    this.stats = stats;
    this.info = info;
    this.affix = affix;
    this.skill = skill;

    this.level = 0;

    const original = values;
    if (values) {
      const showElites = Data.getOption("showElites", state);  
      const elites = (showElites ? 1 + 0.01 * (this.stats.edmg || 0) : 1);
      if (typeof info.info === "function") {
        for (const stat in values) {
          if (typeof values[stat] === "number") {
            if (values === original) values = {...values};
            values[stat] = formatNumber(values[stat] * elites, 0, 10000);
          }
        }
      } else {
        for (const stat in values) {
          if (typeof values[stat] !== "string") continue;
          let expr = values[stat];
          let unmod = undefined;
          if (expr[0] === '@' || expr[0] === '%') {
            unmod = expr[0];
            expr = expr.substring(1);
          }
          expr = execString(expr, this.stats, info.affixid || affix, info.params);
          if (values === original) values = {...values};
          if (typeof expr === "number") {
            if (!unmod) expr *= elites;
            if (unmod === "%") {
              values[stat] = Math.floor(expr) + "%";
            } else {
              values[stat] = formatNumber(expr, 0, 10000);
            }
          } else {
            values[stat] = expr;
          }
        }
      }
    }
    this.source = values;
  }

  values() {
    if (this.level < 1) {
      this.level = 1;
      this._process();
    }
    return this.result;
  }

  text() {
    if (this.level < 2) {
      this.level = 2;
      this._process();
    }
    return this.result;
  }

  tips() {
    if (this.level < 3) {
      this.level = 3;
      this._process();
    }
    return this.result;
  }

  _process() {
    if (!this.source) return;
    this.result = processInfo(this.source, {
      affix: this.info.affixid || this.affix,
      params: this.info.params,
      skill: this.skill,
      state: this.state,
      stats: this.stats,
      notext: this.level < 2,
      notips: this.level < 3,
    });
  }
}

export class InfoCalculator {
  _attacks = {};
  _skills = {};
  _passives = {};
  _gems = {};
  _affixes = {};
  _follower = {};

  constructor(state, stats) {
    this.state = state;
    this.stats = stats;
  }

  attack(id) {
    if (this._attacks[id]) return this._attacks[id];
    const info = Data.skills.attack[id];
    if (!info) return null;
    return this._attacks[id] = new InfoCache(this.state, this.stats, info, {...info.info}, null, null);
  }

  skill(skill, rune) {
    if (!rune) rune = this.stats.skills[skill] || "x";
    const key = `${skill}.${rune}`;
    if (this._skills[key]) return this._skills[key];

    const info = Data.skills[this.stats.charClass][skill];
    if (!info) return null;

    let values;
    if (typeof info.info === "function") {
      values = info.info(rune, this.stats);
    } else if (info.info) {
      values = info.info[rune];
      if (info.info["*"]) {
        values = {...info.info["*"], ...values};
      }
    }
    return this._skills[key] = new InfoCache(this.state, this.stats, info, values, null, [skill, rune]);
  }

  passive(id) {
    if (this._passives[id]) return this._passives[id];
    const info = Data.passives[this.stats.charClass][id];
    if (!info) return null;
    let values;
    if (typeof info.info === "function") {
      values = info.info(this.stats);
    } else {
      values = info.info;
    }
    return this._passives[id] = new InfoCache(this.state, this.stats, info, values, null, null);
  }

  follower(id) {
    if (this._follower[id]) return this._follower[id];

    const info = Data.followerSkills[id];
    if (!info) return null;

    let values;
    if (typeof info.info === "function") {
      values = info.info(this.stats.follower);
    } else if (info.info) {
      values = info.info;
    }
    return this._follower[id] = new InfoCache(this.state, this.stats.follower, info, values, null, null);
  }

  gem(id) {
    if (this._gems[id]) return this._gems[id];
    const info = Data.legendaryGems[id];
    if (!info) return null;
    let values;
    if (typeof info.info === "function") {
      values = info.info(this.stats.gems[id], this.stats);
    } else {
      values = info.info;
    }
    return this._gems[id] = new InfoCache(this.state, this.stats, info, values, null, null);
  }

  affix(id, follower) {
    const key = id + (follower ? "#follower" : "");
    if (this._affixes[key]) return this._affixes[key];
    const info = Data.itemaffixes[id];
    if (!info) return null;
    let values;
    const stats = (follower && this.stats.follower) || this.stats;
    if (typeof info.info === "function") {
      values = info.info(stats.affixes[id].value, stats);
    } else {
      values = info.info;
    }
    return this._affixes[key] = new InfoCache(this.state, stats, info, values, id, null);
  }
}


let infoCache = null;
let infoCacheProfile = null;
let infoCacheActive = null;

export function getSkillInfo(state) {
  if (!state) {
    state = Store.instance.getState();
  }
  const profile = state.profiles[state.activeProfile];
  const active = state.active;
  if (infoCacheProfile === profile && infoCacheActive === active) {
    return infoCache;
  }
  infoCacheProfile = profile;
  infoCacheActive = active;
  return infoCache = new InfoCalculator(state, getStats(state));
}

export function affixShown(affix, stats) {
  if (!stats.affixes.hasOwnProperty(affix)) return false;
  const data = Data.itemaffixes[affix];
  if (!data) return false;
  if (!data.info && data.setActive == null && !data.params) return false;
  if (!data.check) return true;
  if (typeof data.info === "function") {
    if (data.info(stats.affixes[affix].value, stats)) return true;
  } else if (data.info) {
    return true;
  } else if (typeof data.buffs === "function") {
    if (data.buffs(stats.affixes[affix].value, stats)) return true;
  } else if (data.buffs) {
    return true;
  }
  return false;
}

function affixName(affix, profile, stats) {
  const source = stats.affixes[affix];
  if (!source) return null;
  if (source.set) {
    const name = Data.itemSets[source.set].name;
    if (source.pieces) return `${name} \u2013 ${_L`${source.pieces} pieces`}`;
    return name;
  }
  const itemData = source.slot && profile.items[source.slot];
  let item = itemData && Data.itemById[itemData.id];
  const info = Data.itemaffixes[affix];
  if (info?.soulshard && itemData?.gems?.[0]?.id) {
    item = Data.itemById[itemData.gems[0].id];
  }
  if (item) return item.name;
  if (info.shrineBuff && Data.shrineBuffs[info.shrineBuff]) {
    return Data.shrineBuffs[info.shrineBuff].name;
  }
  return null;
}

export function getSkillValues(state) {
  const cache = getSkillInfo(state);
  const stats = cache.stats;

  const values = src => {
    const dst = {};
    for (const key in src) {
      if (typeof src[key].value === "number") {
        dst[key] = src[key].value;
      }
    }
    return dst;
  };

  const skillValues = {};
  const add = (name, data, source) => {
    const vals = data.text();
    if (vals) {
      skillValues[name] = {data: values(vals), ...source};
    }
  };

  const profile = state.profiles[state.activeProfile];

  for (const skill of profile.skills) {
    const data = skill && cache.skill(skill[0], skill[1]);
    if (data) add(data.info.name, data, {skill});
  }

  for (const passive of profile.passives) {
    const data = passive && cache.passive(passive);
    if (data) add(data.info.name, data, {passive});
  }

  if (stats.extra_passive) {
    for (const passive of stats.extra_passive) {
      const data = cache.passive(passive);
      if (data) add(data.info.name, data, {passive});
    }
  }

  const kanaiFx = [];
  for (const [slot, id] of Object.entries(profile.kanai)) {
    if (!cubeSlotValid(profile, slot)) continue;
    const affix = id && cubeGetStat(id);
    if (!affix) continue;
    kanaiFx.push(affix);
    const data = cache.affix(affix);
    if (data) add(Data.itemById[id].name, data, {kanai: affix});
  }

  for (const gem in stats.gems) {
    const data = cache.gem(gem);
    if (data) add(data.info.name, data, {gem});
  }

  for (const affix in stats.affixes) {
    if (kanaiFx.includes(affix) || !affixShown(affix, stats)) continue;
    const data = cache.affix(affix);
    const name = data && affixName(affix, profile, stats);
    if (data && name) add(name, data, {affix});
  }

  return skillValues;
}

export function getAttackType(profile) {
  let mh = profile.items.mainhand?.id;
  mh = mh && Data.itemById[mh.id]?.type;
  return ((mh && Data.itemTypes[mh].attack) || "hand");
}

export function altarToggle(nodes, id) {
  if (nodes.includes(id)) {
    const result = [];
    for (const oid of nodes) {
      if (oid === id) continue;
      const node = Data.altar[oid];
      if (node.requires && !node.requires.some(r => result.includes(r))) continue;
      result.push(oid);
    }
    return result;
  } else {
    const node = Data.altar[id];
    const result = nodes.slice();
    if (!node.requires || node.requires.some(r => result.includes(r))) {
      result.push(id);
    }
    return result;
  }
}
